import { BeneficiaryDesignationsFormShape } from '@/modules/beneficiaryDesignations/BeneficiaryDesignationsForm.types';
import { DispositiveProvisionsForm_DispositionScenario } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { DispositiveProvisionsFormV2_DispositionScenario } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { Fields as BasicInformationSubformFieldsType } from '@/modules/entities/BasicInformationSubform/BasicInformationSubform.types';
import { Fields as BusinessEntityDetailsSubformFieldsType } from '@/modules/entities/BusinessEntityDetailsSubform/BusinessEntityDetailsSubform.types';
import { EntityBeneficiariesSubformFields } from '@/modules/entities/EntityBeneficiariesSubform/EntityBeneficiariesSubform.types';
import { SubformConfig } from '@/modules/entities/EntitySubforms/entitySubformConfigs';
import { Fields as TaxStatusSubformFieldsType } from '@/modules/entities/TaxStatusSubform/TaxStatusSubform.types';
import { Fields as TrustDetailsSubformFieldsType } from '@/modules/entities/TrustDetailsSubform/TrustDetailsSubform.types';

// These mostly map to SubformConfig types, but are specific to the UX where
// users edit the entity in sections with the split-screen document view,
// instead of the single large form.
export enum EditEntitySection {
  BASIC_INFORMATION = 'BASIC_INFORMATION',
  TRUSTEES = 'TRUSTEES',
  TAX_STATUS = 'TAX_STATUS',
  BENEFICIARIES = 'BENEFICIARIES', // GRAT (even though they are called "remainder", we're still using the regular beneficiariesSubform
  LIFETIME_BENEFICIARIES = 'LIFETIME_BENEFICIARIES', // SLAT
  REMAINDER_BENEFICIARIES = 'REMAINDER_BENEFICIARIES', // CRT, CLT
  INCOME_BENEFICIARIES = 'INCOME_BENEFICIARIES', // CRT
  LEAD_BENEFICIARIES = 'LEAD_BENEFICIARIES', // CLT
  INSURANCE_POLICIES = 'INSURANCE_POLICIES', // ILIT
  INITIAL_FUNDING_ASSETS = 'INITIAL_FUNDING_ASSETS', // GRAT, QPRT,
  ANNUITY_PAYMENTS = 'ANNUITY_PAYMENTS', // GRAT
  BUSINESS_ENTITY_TAX_STATUS = 'BUSINESS_ENTITY_TAX_STATUS', // Business Entities
  BUSINESS_ENTITY_DETAILS = 'BUSINESS_ENTITY_DETAILS', // Business Entities
  DISPOSITIVE_PROVISIONS_GRANTOR_1 = 'DISPOSITIVE_PROVISIONS_GRANTOR_1',
  DISPOSITIVE_PROVISIONS_GRANTOR_2 = 'DISPOSITIVE_PROVISIONS_GRANTOR_2',
  BENEFICIARY_DESIGNATIONS = 'BENEFICIARY_DESIGNATIONS',
}

export function isEditEntitySection(
  value: string | null | undefined
): value is EditEntitySection {
  return Object.values(EditEntitySection).includes(value as EditEntitySection);
}

export function isDPSection(section: EditEntitySection | undefined): boolean {
  return (
    section === EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1 ||
    section === EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_2
  );
}

export function isBDGSection(section: EditEntitySection | undefined): boolean {
  return section === EditEntitySection.BENEFICIARY_DESIGNATIONS;
}

export interface EditEntityRichSection {
  label: string;
  /** The section to switch to when this option is selected. */
  section: EditEntitySection;
  /**
   * All sections this option represents. The consolidated "Dispositions"
   * option (DISPOSITIVE_PROVISIONS_GRANTOR_1) also represents
   * DISPOSITIVE_PROVISIONS_GRANTOR_2, which has no menu entry of its own
   * because the death order is selected within the DP form. Lookups that
   * resolve the current section against the options (switcher label, selected
   * check mark, prev/next navigation) should match on this field.
   */
  representedSections: EditEntitySection[];
}

// The individual subform field types for each supported section/subform type.
export type SupportedSubformFieldTypes =
  | BasicInformationSubformFieldsType
  | TrustDetailsSubformFieldsType
  | TaxStatusSubformFieldsType
  | EntityBeneficiariesSubformFields
  | BusinessEntityDetailsSubformFieldsType;

export interface CombinedSupportedSubformFieldTypes {
  basicInformationSubform?: BasicInformationSubformFieldsType['basicInformationSubform'];
  trustDetailsSubform?: TrustDetailsSubformFieldsType['trustDetailsSubform'];
  taxStatusSubform?: TaxStatusSubformFieldsType['taxStatusSubform'];
  entityBeneficiariesSubform?: EntityBeneficiariesSubformFields['beneficiariesSubform'];
  businessEntityDetailsSubform?: BusinessEntityDetailsSubformFieldsType['businessEntityDetailsSubform'];
  beneficiariesSubform?: EntityBeneficiariesSubformFields['beneficiariesSubform'];
  lifetimeBeneficiariesSubform?: EntityBeneficiariesSubformFields['lifetimeBeneficiariesSubform'];
  remainderBeneficiariesSubform?: EntityBeneficiariesSubformFields['remainderBeneficiariesSubform'];
  leadBeneficiarySubform?: EntityBeneficiariesSubformFields['leadBeneficiarySubform'];
  incomeBeneficiariesSubform?: EntityBeneficiariesSubformFields['incomeBeneficiariesSubform'];
  beneficiariesAfterBothGrantorsDie?: EntityBeneficiariesSubformFields['beneficiariesAfterBothGrantorsDie'];
  dispositiveProvisions?: Partial<DispositiveProvisionsForm_DispositionScenario>;
  dispositiveProvisionsV2?: Partial<DispositiveProvisionsFormV2_DispositionScenario>;
  beneficiaryDesignations?: BeneficiaryDesignationsFormShape['beneficiaryDesignations'];
}

// This is a wrapper around the SubformConfig and initial data for the subform,
// for a given EditEntitySection / SubformConfig type.
export interface EditEntitySectionInitialConfigWithData {
  subformConfig: SubformConfig;

  // We're setting up initial data to be like { [NAMESPACE]: {...values} },
  // even though we're only handling one section/subform at a time, so that we
  // can re-use the same code for form validation/submission.
  initialData: SupportedSubformFieldTypes;
}
